import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockLoans } from '../../services/mockData';
import { authService } from '../../services/authService';

const BrokerPipelinePage = () => {
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('loanNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  
  const [visibleColumns, setVisibleColumns] = useState({
    loanNumber: true,
    borrower: true,
    loanAmount: true,
    status: true,
    lockExpiration: true,
    conditions: true
  });

  const currentUser = authService.getCurrentUser();

  useEffect(() => {
    setTimeout(() => {
      // Filter loans for current loan officer
      const userLoans = mockLoans.filter(l => l.loanOfficer === currentUser?.name);
      setLoans(userLoans);
      setLoading(false);
    }, 500);
  }, [currentUser]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleColumn = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const filteredAndSortedLoans = useMemo(() => {
    let filtered = [...loans];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(loan => 
        loan.loanNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loan.borrowerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loan.propertyAddress.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(loan => loan.status === statusFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'loanAmount') {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      } else if (sortField === 'lockExpiration') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (sortField === 'conditionsCount') {
        aVal = parseInt(aVal);
        bVal = parseInt(bVal);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [loans, searchTerm, statusFilter, sortField, sortDirection]);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Approved':
        return 'bg-green';
      case 'Clear to Close':
        return 'bg-teal';
      case 'In Underwriting':
        return 'bg-blue';
      case 'Conditions':
        return 'bg-yellow';
      case 'Processing':
        return 'bg-cyan';
      default:
        return 'bg-gray';
    }
  };

  const isLockExpiringSoon = (lockExpiration) => {
    const expirationDate = new Date(lockExpiration);
    const today = new Date();
    const daysUntilExpiration = Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiration <= 7;
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <i className="ti ti-selector"></i>;
    return sortDirection === 'asc' ? <i className="ti ti-sort-ascending"></i> : <i className="ti ti-sort-descending"></i>;
  };

  const uniqueStatuses = [...new Set(loans.map(l => l.status))];

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-header d-print-none">
      <div className="row g-2 align-items-center mb-3">
        <div className="col">
          <h2 className="page-title">My Pipeline</h2>
          <div className="text-secondary mt-1">Your active loan submissions</div>
        </div>
      </div>

      <div className="row row-deck row-cards mb-3">
        <div className="col-sm-6 col-lg-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="subheader">Active Loans</div>
              </div>
              <div className="h1 mb-0">{filteredAndSortedLoans.length}</div>
              <div className="text-secondary small">of {loans.length} total</div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="subheader">Total Volume</div>
              </div>
              <div className="h1 mb-0">${(filteredAndSortedLoans.reduce((sum, l) => sum + l.loanAmount, 0) / 1000000).toFixed(1)}M</div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="subheader">Pending Conditions</div>
              </div>
              <div className="h1 mb-0">{filteredAndSortedLoans.reduce((sum, l) => sum + l.conditionsCount, 0)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">My Loans</h3>
          <div className="card-actions">
            <div className="input-group input-group-flat me-2" style={{ width: '300px' }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search loans..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="input-group-text">
                <i className="ti ti-search"></i>
              </span>
            </div>
            <select 
              className="form-select form-select-sm me-2" 
              style={{ width: 'auto' }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <div className="dropdown">
              <button 
                className="btn btn-sm btn-outline-secondary dropdown-toggle"
                onClick={() => setShowColumnsMenu(!showColumnsMenu)}
              >
                <i className="ti ti-columns me-1"></i>
                Columns
              </button>
              {showColumnsMenu && (
                <div className="dropdown-menu dropdown-menu-end show" style={{ position: 'absolute', right: 0 }}>
                  {Object.entries(visibleColumns).map(([key, value]) => (
                    <label key={key} className="dropdown-item">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={value}
                        onChange={() => toggleColumn(key)}
                      />
                      {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="card-body">
          {filteredAndSortedLoans.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">
                <i className="ti ti-search fs-1"></i>
              </div>
              <p className="empty-title">No loans found</p>
              <p className="empty-subtitle text-secondary">
                {loans.length === 0 ? 'Your submitted loans will appear here' : 'No loans match your search criteria'}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    {visibleColumns.loanNumber && (
                      <th className="cursor-pointer" onClick={() => handleSort('loanNumber')}>
                        <div className="d-flex align-items-center">
                          Loan # {getSortIcon('loanNumber')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.borrower && (
                      <th className="cursor-pointer" onClick={() => handleSort('borrowerName')}>
                        <div className="d-flex align-items-center">
                          Borrower {getSortIcon('borrowerName')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.loanAmount && (
                      <th className="cursor-pointer" onClick={() => handleSort('loanAmount')}>
                        <div className="d-flex align-items-center">
                          Loan Amount {getSortIcon('loanAmount')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.status && (
                      <th className="cursor-pointer" onClick={() => handleSort('status')}>
                        <div className="d-flex align-items-center">
                          Status {getSortIcon('status')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.lockExpiration && (
                      <th className="cursor-pointer" onClick={() => handleSort('lockExpiration')}>
                        <div className="d-flex align-items-center">
                          Lock Expiration {getSortIcon('lockExpiration')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.conditions && (
                      <th className="cursor-pointer" onClick={() => handleSort('conditionsCount')}>
                        <div className="d-flex align-items-center">
                          Conditions {getSortIcon('conditionsCount')}
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedLoans.map((loan) => (
                    <tr 
                      key={loan.id}
                      onClick={() => navigate(`/broker/loans/${loan.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      {visibleColumns.loanNumber && (
                        <td>
                          <div className="d-flex align-items-center">
                            <span className="fw-bold">{loan.loanNumber}</span>
                            {loan.isRecentlyApproved && (
                              <span className="badge bg-green-lt ms-2">
                                <i className="ti ti-check me-1"></i>
                                Recently Approved
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      {visibleColumns.borrower && (
                        <td>
                          <div>
                            <div className="fw-bold">{loan.borrowerName}</div>
                            <div className="text-secondary small">{loan.propertyAddress}</div>
                          </div>
                        </td>
                      )}
                      {visibleColumns.loanAmount && (
                        <td>${loan.loanAmount.toLocaleString()}</td>
                      )}
                      {visibleColumns.status && (
                        <td>
                          <span className={`badge ${getStatusBadgeClass(loan.status)}`}>
                            {loan.status}
                          </span>
                        </td>
                      )}
                      {visibleColumns.lockExpiration && (
                        <td>
                          <div className={isLockExpiringSoon(loan.lockExpiration) ? 'text-yellow fw-bold' : ''}>
                            {new Date(loan.lockExpiration).toLocaleDateString()}
                            {isLockExpiringSoon(loan.lockExpiration) && (
                              <i className="ti ti-alert-circle ms-1"></i>
                            )}
                          </div>
                        </td>
                      )}
                      {visibleColumns.conditions && (
                        <td>
                          {loan.conditionsCount > 0 ? (
                            <span className="badge bg-yellow-lt">{loan.conditionsCount}</span>
                          ) : (
                            <span className="text-secondary">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrokerPipelinePage;
